import { ArrowLeft, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import AddItemsStep from '../components/wizard/AddItemsStep';
import CustomerInfoStep from '../components/wizard/CustomerInfoStep';
import ReviewOrderStep from '../components/wizard/ReviewOrderStep';
import { OrderStatus } from '../types/order';
import { OrderItemCreateInput } from '../types/orderItem';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

export type OrderFormData = {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_contact: string;
  order_date: string;
  required_by_date: string;
  billing_address_id: string;
  delivery_address_id: string;
  special_instructions: string;
  status: OrderStatus;
  billing_address?: {
    address_line1: string;
    address_line2: string;
    city: string;
    region: string;
    postal_code: string;
    country_code: string;
  };
  delivery_address?: {
    address_line1: string;
    address_line2: string;
    city: string;
    region: string;
    postal_code: string;
    country_code: string;
  };
};

const DRAFT_KEY = 'homebound_order_draft';

const CreateOrder = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [showResumeDraftModal, setShowResumeDraftModal] = useState(false);

  const [orderData, setOrderData] = useState<OrderFormData>({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_contact: '',
    order_date: new Date().toISOString().split('T')[0],
    required_by_date: '',
    billing_address_id: '',
    delivery_address_id: '',
    special_instructions: '',
    status: OrderStatus.CONFIRMED,
  });

  const [orderItems, setOrderItems] = useState<OrderItemCreateInput[]>([]);

  // Load user and check for existing draft on mount
  useEffect(() => {
    // Load user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    // Check for existing draft
    const savedDraft = localStorage.getItem(DRAFT_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        // Show modal to resume or start fresh
        setShowResumeDraftModal(true);
      } catch (error) {
        console.error('Error loading draft:', error);
        localStorage.removeItem(DRAFT_KEY);
      }
    }
  }, []);

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const token = localStorage.getItem('token');

      // Create addresses first if they're new
      let billingAddressId = orderData.billing_address_id;
      let deliveryAddressId = orderData.delivery_address_id;

      if (orderData.billing_address && orderData.billing_address.address_line1) {
        const billingResponse = await fetch('/api/addresses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData.billing_address),
        });
        const billingResult = await billingResponse.json();
        if (billingResult.success) {
          billingAddressId = billingResult.data.id;
        }
      }

      if (orderData.delivery_address && orderData.delivery_address.address_line1) {
        const deliveryResponse = await fetch('/api/addresses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData.delivery_address),
        });
        const deliveryResult = await deliveryResponse.json();
        if (deliveryResult.success) {
          deliveryAddressId = deliveryResult.data.id;
        }
      }

      const payload = {
        customer_name: orderData.customer_name || 'Draft Order',
        customer_email: orderData.customer_email,
        customer_phone: orderData.customer_phone,
        customer_contact: orderData.customer_contact,
        order_date: orderData.order_date,
        required_by_date: orderData.required_by_date,
        billing_address_id: billingAddressId || null,
        delivery_address_id: deliveryAddressId || null,
        special_instructions: orderData.special_instructions,
        status: OrderStatus.DRAFT,
        items: orderItems,
      };

      let response;
      if (draftId) {
        // Update existing draft
        response = await fetch(`/api/orders/${draftId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new draft
        response = await fetch('/api/orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to save draft');
      }

      // Save draft ID and data to localStorage as backup
      setDraftId(result.data.id);
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          draftId: result.data.id,
          orderData,
          orderItems,
          currentStep,
          savedAt: new Date().toISOString(),
        })
      );

      alert('Draft saved successfully!');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert(error instanceof Error ? error.message : 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const loadDraft = () => {
    const savedDraft = localStorage.getItem(DRAFT_KEY);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setDraftId(draft.draftId);
        setOrderData(draft.orderData);
        setOrderItems(draft.orderItems);
        setCurrentStep(draft.currentStep);
        setShowResumeDraftModal(false);
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraftId(null);
    setShowResumeDraftModal(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      // Create addresses first if they're new
      let billingAddressId = orderData.billing_address_id;
      let deliveryAddressId = orderData.delivery_address_id;

      if (orderData.billing_address && orderData.billing_address.address_line1) {
        const billingResponse = await fetch('/api/addresses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData.billing_address),
        });
        const billingResult = await billingResponse.json();
        if (billingResult.success) {
          billingAddressId = billingResult.data.id;
        }
      }

      if (orderData.delivery_address && orderData.delivery_address.address_line1) {
        const deliveryResponse = await fetch('/api/addresses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderData.delivery_address),
        });
        const deliveryResult = await deliveryResponse.json();
        if (deliveryResult.success) {
          deliveryAddressId = deliveryResult.data.id;
        }
      }

      const payload = {
        customer_name: orderData.customer_name,
        customer_email: orderData.customer_email,
        customer_phone: orderData.customer_phone,
        customer_contact: orderData.customer_contact,
        order_date: orderData.order_date,
        required_by_date: orderData.required_by_date,
        billing_address_id: billingAddressId,
        delivery_address_id: deliveryAddressId,
        special_instructions: orderData.special_instructions,
        status: orderData.status,
        items: orderItems,
      };

      let response;
      if (draftId) {
        // Update the draft to make it a real order
        response = await fetch(`/api/orders/${draftId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new order
        response = await fetch('/api/orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create order');
      }

      // Clear draft after successful order creation
      clearDraft();

      navigate(`/orders/${result.data.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      alert(error instanceof Error ? error.message : 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => sum + item.quantity * item.unit_price * item.rental_length_months, 0);
  };

  const canProceedToStep2 = () => {
    return orderData.customer_name && orderData.customer_email && orderData.customer_phone;
  };

  const canProceedToStep3 = () => {
    return orderItems.length > 0;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Orders
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Create New Order</CardTitle>
            <CardDescription>
              {currentStep === 1 && 'Step 1: Enter customer details and delivery information'}
              {currentStep === 2 && 'Step 2: Add products to this order'}
              {currentStep === 3 && 'Step 3: Review and confirm order details'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {currentStep === 1 && (
              <CustomerInfoStep data={orderData} onChange={setOrderData} />
            )}

            {currentStep === 2 && <AddItemsStep items={orderItems} onChange={setOrderItems} />}

            {currentStep === 3 && (
              <ReviewOrderStep
                orderData={orderData}
                items={orderItems}
                total={calculateTotal()}
              />
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <div className="flex gap-2">
            {currentStep === 1 ? (
              <Button variant="outline" onClick={() => navigate('/orders')}>
                Cancel
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setCurrentStep((prev) => (prev - 1) as 1 | 2 | 3)}
                disabled={submitting || savingDraft}
              >
                Back
              </Button>
            )}

            <Button variant="secondary" onClick={saveDraft} disabled={submitting || savingDraft}>
              <Save className="h-4 w-4 mr-2" />
              {savingDraft ? 'Saving...' : 'Save Draft'}
            </Button>
          </div>

          <div>
            {currentStep === 1 && (
              <Button onClick={() => setCurrentStep(2)} disabled={!canProceedToStep2()}>
                Next: Add Items
              </Button>
            )}

            {currentStep === 2 && (
              <Button onClick={() => setCurrentStep(3)} disabled={!canProceedToStep3()}>
                Next: Review Order
              </Button>
            )}

            {currentStep === 3 && (
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Creating Order...' : 'Create Order'}
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Resume Draft Modal */}
      {showResumeDraftModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-xl font-bold mb-4">Resume Draft Order?</h3>
            <p className="text-gray-600 mb-6">
              You have an unsaved draft from a previous session. Would you like to continue editing
              it or start fresh?
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={clearDraft}>
                Start Fresh
              </Button>
              <Button onClick={loadDraft}>Resume Draft</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateOrder;

